import { useState, useEffect, useCallback } from 'react';
import ConfigurationService from '../services/ConfigurationService';
import { useInjectedService } from './useInjectedService';

/**
 * NetworkStatus Interface
 * Represents the current network state in a platform-agnostic way.
 */
export interface NetworkStatus {
    /** Whether the system reports being online */
    isOnline: boolean;
    /** Whether we've just reconnected (transient state) */
    wasOffline: boolean;
    /** Timestamp of last status change */
    lastChanged: number;
}

/**
 * UseNetworkStatusOptions Interface
 * Callbacks for network transition events.
 */
export interface UseNetworkStatusOptions {
    /** Callback when connection is restored */
    onOnline?: () => void;
    /** Callback when connection is lost */
    onOffline?: () => void;
}

/**
 * useNetworkStatus Hook - Platform Agnostic
 * 
 * Provides real-time network status detection with reconnection callbacks.
 * Consumes the injected IConnectivityService for multi-platform compatibility.
 * Replaces direct use of navigator.onLine and window listeners.
 * 
 * @param options - Optional callbacks for online/offline events
 * @returns NetworkStatus object with current network state
 */
export function useNetworkStatus(options: UseNetworkStatusOptions = {}): NetworkStatus {
    const { onOnline, onOffline } = options;
    const connectivityService = useInjectedService('connectivity');

    // Default to true and update immediately on mount to prevent flashing
    const [status, setStatus] = useState<NetworkStatus>({
        isOnline: true,
        wasOffline: false,
        lastChanged: Date.now(),
    });

    const handleOnline = useCallback(() => {
        setStatus((prev) => ({
            isOnline: true,
            wasOffline: !prev.isOnline, // Mark transition if it was offline
            lastChanged: Date.now(),
        }));
        onOnline?.();
    }, [onOnline]);

    const handleOffline = useCallback(() => {
        setStatus({
            isOnline: false,
            wasOffline: false,
            lastChanged: Date.now(),
        });
        onOffline?.();
    }, [onOffline]);

    useEffect(() => {
        let isMounted = true;

        // 1. Fetch initial status from Universal Connectivity Service
        connectivityService.isOnline().then((online) => {
            if (isMounted) {
                setStatus((prev) => ({
                    ...prev,
                    isOnline: online,
                    lastChanged: Date.now(),
                }));
            }
        });

        // 2. Subscribe to universal network status changes
        const unsubscribe = connectivityService.subscribe((online) => {
            if (!isMounted) return;
            if (online) {
                handleOnline();
            } else {
                handleOffline();
            }
        });

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, [connectivityService, handleOnline, handleOffline]);

    // Clear wasOffline flag after configurable delay (Enterprise Resilience)
    useEffect(() => {
        if (status.wasOffline) {
            const timeout = ConfigurationService.getReconnectedTimeout();
            const timer = setTimeout(() => {
                setStatus((prev) => ({ ...prev, wasOffline: false }));
            }, timeout);
            return () => clearTimeout(timer);
        }
    }, [status.wasOffline]);

    return status;
}

/**
 * Simple hook that just returns online status boolean.
 * For components that only need basic online/offline check.
 */
export function useIsOnline(): boolean {
    const { isOnline } = useNetworkStatus();
    return isOnline;
}

/**
 * Gets the configured offline message from ConfigurationService.
 */
export function getOfflineMessage(): string {
    return ConfigurationService.getOfflineMessage();
}

export default useNetworkStatus;
